import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, Flame, Plus, RotateCcw, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { gewohnheitenAPI } from '@/lib/api';

/* Gewohnheiten (13.10.2026, replica/coaching-recherche/gap.md W4): kleine tägliche Aufgaben neben dem Training.
   Der Coach weist zu (hier), das Mitglied hakt in der App ab. Dieselbe Karte im CRM (Lead › Kraftlog) und auf /coach —
   die Aufrufe kommen als Props, weil /coach eine eigene Anmeldung hat.
   W3.3 (29.10.2026): Rhythmus täglich oder an Wochentagen, 7-Tage-Streifen mit Zuständen (`zustand` vom Server:
   erledigt · offen · heute offen · nicht aktiv · pausiert = krank gemeldet), Treue in %, aktiv ja/nein — ausgeschaltete
   stehen darunter und lassen sich wieder einschalten (`aktivSetzen`, optional). */

const TAG = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const zahl = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('de-DE', { maximumFractionDigits: 2 }));
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

const ZUSTAND = {
  erledigt: { text: 'erledigt', cls: 'bg-primary' },
  offen: { text: 'offen', cls: 'bg-muted-foreground/40' },
  heute_offen: { text: 'heute offen', cls: 'bg-transparent ring-2 ring-primary' },
  nicht_aktiv: { text: 'nicht aktiv', cls: 'bg-transparent ring-1 ring-border' },
  pausiert: { text: 'pausiert (krank)', cls: 'bg-amber-500/70' },
};
/* Ältere Antworten ohne `zustand`: aus den alten Feldern ableiten */
const zustandVon = (t, heute) => t.zustand || (t.erfuellt ? 'erledigt' : !t.faellig ? 'nicht_aktiv' : t.pausiert ? 'pausiert' : t.datum === heute ? 'heute_offen' : 'offen');
const rhythmus = (g) => (g.je_woche ? `${g.je_woche}× je Woche` : (!g.tage || g.tage.length === 7) ? 'täglich' : g.tage.map((i) => TAG[i]).join(', '));

function Punkte({ verlauf, heute }) {
  return (
    <span className="flex gap-1" aria-label={`Letzte sieben Tage: ${verlauf.map((t) => `${TAG[(new Date(`${t.datum}T12:00:00Z`).getUTCDay() + 6) % 7]} ${ZUSTAND[zustandVon(t, heute)].text}`).join(', ')}`}>
      {verlauf.map((t) => {
        const z = zustandVon(t, heute);
        return <span key={t.datum} title={`${t.datum}: ${ZUSTAND[z].text}`} data-zustand={z} className={`h-3 w-3 rounded-full ${ZUSTAND[z].cls}`} />;
      })}
    </span>
  );
}

function Legende() {
  return (
    <p className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {Object.entries(ZUSTAND).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${v.cls}`} />{v.text}</span>
      ))}
    </p>
  );
}

function Wochentage({ tage, onChange }) {
  const alle = tage.length === 7;
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Rhythmus">
      <button type="button" onClick={() => onChange([0, 1, 2, 3, 4, 5, 6])} aria-pressed={alle}
        className={`h-11 rounded-md border px-3 text-sm ${alle ? 'border-primary bg-primary text-primary-foreground' : 'border-input'}`}>täglich</button>
      {TAG.map((t, i) => {
        const an = tage.includes(i);
        return (
          <button key={t} type="button" aria-pressed={an} aria-label={t}
            onClick={() => { const neu = an ? tage.filter((x) => x !== i) : [...tage, i].sort(); if (neu.length) onChange(neu); }}
            className={`h-11 w-11 rounded-md border text-sm ${an && !alle ? 'border-primary bg-primary text-primary-foreground' : an ? 'border-primary/50' : 'border-input text-muted-foreground'}`}>{t}</button>
        );
      })}
    </div>
  );
}

export default function Gewohnheiten({ laden, anlegen, beenden, aktivSetzen, testId = 'gewohnheiten' }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [neu, setNeu] = useState(null);         // { vorlage, titel, art, einheit, ziel, tage }
  const [busy, setBusy] = useState(false);
  const lade = useCallback(() => {
    laden().then((r) => { setD(r.data); setErr(''); }).catch((e) => setErr(fehlerText(e, 'Gewohnheiten nicht geladen')));
  }, [laden]);
  useEffect(() => { lade(); }, [lade]);

  const speichern = async () => {
    setBusy(true);
    try {
      const body = neu.vorlage ? { vorlage: neu.vorlage, ziel: neu.art === 'zahl' ? Number(String(neu.ziel).replace(',', '.')) : undefined }
        : { titel: neu.titel, art: neu.art, einheit: neu.einheit, ziel: neu.art === 'zahl' ? Number(String(neu.ziel).replace(',', '.')) : undefined };
      if (neu.tage && neu.tage.length < 7 && !neu.je_woche) body.tage = neu.tage;
      await anlegen(body);
      toast.success('Gewohnheit eingerichtet – das Mitglied sieht sie in der App');
      setNeu(null); lade();
    } catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
    setBusy(false);
  };
  const stoppen = async (g) => {
    if (!window.confirm(`„${g.titel}“ ausschalten? Der Verlauf bleibt gespeichert${aktivSetzen ? ', wieder einschalten geht jederzeit' : ''}.`)) return;
    try { await (aktivSetzen ? aktivSetzen(g.id, false) : beenden(g.id)); toast.success('Ausgeschaltet'); lade(); } catch (e) { toast.error(fehlerText(e, 'Nicht ausgeschaltet')); }
  };
  const einschalten = async (g) => {
    try { await aktivSetzen(g.id, true); toast.success('Wieder aktiv'); lade(); } catch (e) { toast.error(fehlerText(e, 'Nicht eingeschaltet')); }
  };

  return (
    <Card className="border-border/60" data-testid={testId}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-base">Gewohnheiten</CardTitle>
          <p className="text-xs text-muted-foreground">Tägliche Kleinigkeiten neben dem Training – das Mitglied hakt sie in der App ab.</p>
        </div>
        {d && !neu && d.gewohnheiten.length < d.max && (
          <Button size="sm" variant="outline" className="h-11 gap-1.5" onClick={() => setNeu({ vorlage: '', titel: '', art: 'haken', einheit: '', ziel: '', tage: [0, 1, 2, 3, 4, 5, 6] })} data-testid={`${testId}-neu`}>
            <Plus className="h-3.5 w-3.5" /> Gewohnheit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {err && <p className="text-sm text-destructive" role="alert">{err}</p>}
        {!d && !err && <Skeleton className="h-16" />}
        {d && !d.gewohnheiten.length && !neu && <p className="text-sm text-muted-foreground">Noch keine. Gut zum Start: Wasser, Schritte oder Schlaf.</p>}
        {d && d.gewohnheiten.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Legende />
            {d.treue != null && <span className="text-xs font-semibold" data-testid={`${testId}-treue`}>Ø Treue {d.treue} %</span>}
          </div>
        )}
        {d && d.gewohnheiten.map((g) => (
          <div key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/50 pt-2 first:border-t-0 first:pt-0">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold break-words">{g.titel}{g.art === 'zahl' && <span className="font-normal text-muted-foreground"> · Ziel {zahl(g.ziel)} {g.einheit}</span>}</p>
              <p className="text-xs text-muted-foreground">
                {g.heute.erfuellt ? <span className="inline-flex items-center gap-0.5 text-foreground"><Check className="h-3 w-3" /> heute geschafft</span> : g.heute.pausiert ? 'heute pausiert (krank)' : g.heute.faellig ? 'heute offen' : 'heute frei'}
                {g.heute.wert !== null && g.art === 'zahl' && ` (${zahl(g.heute.wert)})`}
                {` · ${rhythmus(g)}`}
                {(g.treue ?? g.woche.quote) !== null && (g.treue ?? g.woche.quote) !== undefined && ` · Treue ${g.treue ?? g.woche.quote} %`}
                {g.von === 'mitglied' && ' · selbst angelegt'}
              </p>
            </div>
            <Punkte verlauf={g.verlauf} heute={d.datum} />
            <span className="inline-flex w-12 items-center justify-end gap-0.5 text-sm font-bold tabular-nums" title="Serie: fällige Tage in Folge geschafft">
              <Flame className="h-3.5 w-3.5 text-primary" aria-hidden="true" />{g.serie}
            </span>
            <Button size="icon" variant="ghost" className="h-11 w-11" aria-label={`${g.titel} ausschalten`} title="Ausschalten (aktiv: nein)" onClick={() => stoppen(g)}><X className="h-4 w-4" /></Button>
          </div>
        ))}
        {d && aktivSetzen && d.beendete?.length > 0 && (
          <details className="rounded-md border border-border/60 p-2 text-sm" data-testid={`${testId}-beendete`}>
            <summary className="cursor-pointer text-xs text-muted-foreground">Ausgeschaltet ({d.beendete.length})</summary>
            <ul className="mt-2 space-y-1">
              {d.beendete.map((b) => (
                <li key={b.id} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 break-words">{b.titel}<span className="text-xs text-muted-foreground"> · bis {b.ende ? new Date(`${b.ende}T12:00:00`).toLocaleDateString('de-DE') : '—'}</span></span>
                  <Button size="sm" variant="outline" className="h-11 gap-1" disabled={d.gewohnheiten.length >= d.max} onClick={() => einschalten(b)}><RotateCcw className="h-3.5 w-3.5" /> Wieder aktiv</Button>
                </li>
              ))}
            </ul>
          </details>
        )}
        {neu && d && (
          <div className="space-y-2 rounded-md border border-border p-3" data-testid={`${testId}-formular`}>
            <label className="block text-xs font-medium" htmlFor={`${testId}-vorlage`}>Vorlage</label>
            <select id={`${testId}-vorlage`} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" value={neu.vorlage}
              onChange={(e) => { const v = d.vorlagen.find((x) => x.key === e.target.value); setNeu(v ? { vorlage: v.key, titel: v.titel, art: v.art, einheit: v.einheit, ziel: v.ziel ?? '', tage: v.tage?.length ? v.tage : [0, 1, 2, 3, 4, 5, 6], je_woche: v.je_woche || null } : { vorlage: '', titel: '', art: 'haken', einheit: '', ziel: '', tage: [0, 1, 2, 3, 4, 5, 6] }); }}>
              <option value="">Eigene Gewohnheit …</option>
              {d.vorlagen.map((v) => <option key={v.key} value={v.key}>{v.titel}</option>)}
            </select>
            {!neu.vorlage && (
              <>
                <Input aria-label="Titel" maxLength={60} placeholder="z. B. Treppe statt Aufzug" value={neu.titel} onChange={(e) => setNeu({ ...neu, titel: e.target.value })} />
                <div className="flex gap-2 text-sm">
                  {[['haken', 'Abhaken'], ['zahl', 'Mit Zahl']].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1.5"><input type="radio" className="accent-marke" checked={neu.art === k} onChange={() => setNeu({ ...neu, art: k })} /> {l}</label>
                  ))}
                </div>
              </>
            )}
            {neu.art === 'zahl' && (
              <div className="flex gap-2">
                <Input aria-label="Ziel" inputMode="decimal" className="w-28" value={neu.ziel} onChange={(e) => setNeu({ ...neu, ziel: e.target.value })} />
                <Input aria-label="Einheit" maxLength={20} placeholder="Einheit" value={neu.einheit} disabled={!!neu.vorlage} onChange={(e) => setNeu({ ...neu, einheit: e.target.value })} />
              </div>
            )}
            {neu.je_woche ? <p className="text-xs text-muted-foreground">Rhythmus aus der Vorlage: {neu.je_woche}× je Woche</p>
              : <div className="space-y-1"><span className="block text-xs font-medium">Rhythmus</span><Wochentage tage={neu.tage || [0, 1, 2, 3, 4, 5, 6]} onChange={(tage) => setNeu({ ...neu, tage })} /></div>}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setNeu(null)}>Abbrechen</Button>
              <Button size="sm" disabled={busy || (!neu.vorlage && !neu.titel.trim())} onClick={speichern}>Einrichten</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* CRM: Lead › Kraftlog (Recht training.view wie der Kraftlog). */
export function GewohnheitenCrm({ leadId }) {
  const f = useMemo(() => ({
    laden: () => gewohnheitenAPI.stand(leadId),
    anlegen: (body) => gewohnheitenAPI.anlegen(leadId, body),
    beenden: (gid) => gewohnheitenAPI.beenden(gid),
    aktivSetzen: (gid, aktiv) => gewohnheitenAPI.aktiv(gid, aktiv),
  }), [leadId]);
  return <Gewohnheiten key={leadId} {...f} />;
}
